import { useFieldState } from 'informed';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { Input } from './Input';
import { PasswordStrengthChecker } from './PasswordStrengthChecker';
import { Validator } from '@/helpers/validators';
import { useToggle } from '@/hooks/common';

interface PasswordProps {
  name: string;
  validate?: Validator<any, any>;
  error?: React.ReactNode;
  validateOn?:
    | 'change'
    | 'blur'
    | 'change-blur'
    | 'change-submit'
    | 'blur-submit'
    | 'submit';
  strengthChecker?: boolean;
}

export const Password: FC<PasswordProps> = ({ strengthChecker, ...props }) => {
  const { t } = useTranslation('common');
  const { images } = useTheme();
  const password = useFieldState<string | undefined>(props.name);

  const [showPassword, toggleShowPassword] = useToggle(false);

  return (
    <ScPassword>
      <ScPasswordInputContainer>
        <Input
          placeholder={t('auth.registration.form.password')}
          type={showPassword ? 'text' : 'password'}
          autoComplete={props.name}
          error={props.error}
          {...props}
        />
        <ScButtonEye
          type="button"
          active={showPassword}
          onClick={toggleShowPassword}
          aria-label="show password"
        >
          {showPassword ? (
            <images.eyeOpenIcon.component />
          ) : (
            <images.eyeClosedIcon.component />
          )}
        </ScButtonEye>
        {password.value?.length > 0 && strengthChecker && password.dirty && (
          <PasswordStrengthChecker name={props.name} />
        )}
      </ScPasswordInputContainer>
    </ScPassword>
  );
};

const ScPassword = styled.div`
  position: relative;
`;

const ScPasswordInputContainer = styled.div`
  position: relative;

  input {
    padding: 8px 48px 8px 16px;
  }
`;

export const ScButtonEye = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 15px;
  border: none;
  position: absolute;
  right: 16px;
  top: 13px;
  cursor: pointer;
  background: none;

  &:hover {
    svg path {
      fill: black;
    }
  }
`;
